import { Button } from "@components/atoms/Button"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Themed } from "@components/atoms/Themed"
import { FlowgramImage } from "@components/site/FlowgramImage"
import { cn } from "@utils/tw"
import { defineBlock, EditableText, useBlockContext, useInlineEditableValue, usePostTitleEditor } from "eddev/blocks"
import { Link } from "eddev/routing"
import { ReactNode, useMemo } from "react"
import { useIntersectionObserver } from "usehooks-ts"

export const meta: BlockMeta = {
  title: "Case Study Details",
  icon: "block-default",
  postMetaBlock: {
    fieldName: "pageHeaderCaseStudy",
    postTypes: ["case-study"],
  },
  conditions: {
    postTypes: ["case-study"],
  },
}

export default defineBlock("content/page-header-case-study", (props) => {
  const store = useBlockContext()

  function hasInline(key: string) {
    if (env.admin) return true
    if (store?.getValue(key)) return true
    return false
  }

  let projectSummary: ReactNode = (
    <div className="w-full self-stretch">
      <div className="w-full md:w-4/5 p-6 flex flex-col gap-5 bg-black text-white rounded-lg self-stretch">
        <EditableText
          as="p"
          store="descriptionLabel"
          className="type-label-s-caps"
          placeholder="At a glance"
          defaultValue="At a glance"
        />
        <EditableText
          as="p"
          store="description"
          className="type-body-xl"
          placeholder="Enter a description for the case study."
        />
      </div>
    </div>
  )

  if (!hasInline("description")) projectSummary = null

  const services = props.services || props.caseStudy?.serviceCategories?.nodes || []
  let projectScope: ReactNode = (
    <div className="w-full flex flex-col gap-9 md:gap-7">
      {!!services?.length && (
        <div className="w-full flex flex-col gap-4">
          <EditableText
            as="p"
            store="scopeLabel"
            className="type-label-s-caps"
            placeholder="Scope"
            defaultValue="Scope"
          />
          <div className="w-full flex flex-col gap-1">
            {services?.map(
              (service, index) =>
                !!service && (
                  <p key={index} className="type-title-s">
                    <Link href={`/work/?service[]=${service.slug}`}>{service.name}</Link>
                  </p>
                ),
            )}
          </div>
        </div>
      )}
      {hasInline("projectCompletion") && (
        <div className="w-full flex flex-col gap-4">
          <EditableText
            as="p"
            store="projectCompletionLabel"
            className="type-label-s-caps"
            placeholder="Project Completion"
            defaultValue="Project Completion"
          />
          <EditableText
            as="p"
            store="projectCompletion"
            className="type-title-s"
            placeholder="Enter project completion date"
            defaultValue="Enter project completion date"
          />
        </div>
      )}
    </div>
  )

  if (!hasInline("projectCompletion") && !services?.length) projectScope = null

  let projectStats: ReactNode = (
    <div className="self-stretch max-md:-mx-grid-margin">
      <div
        className="flex max-md:px-grid-margin md:w-full md:flex-nowrap overflow-auto md:overflow-visible no-scrollbars"
        data-transition="children/scale"
      >
        <StatBlock id={1} bg="bg-bark" fg="text-black" />
        <StatBlock id={2} bg="bg-blue" fg="text-black" />
        <StatBlock id={3} bg="bg-black" fg="text-white" />
      </div>
    </div>
  )

  let hasStats = false
  for (let i = 1; i <= 3; i++) {
    if (hasInline(`stat${i}`) || hasInline(`stat${i}Description`)) {
      hasStats = true
      break
    }
  }
  if (!hasStats) projectStats = null

  let clientList = props.clients?.length! > 0 && (
    <div className="w-full self-stretch">
      <div className="w-full md:w-4/5 flex flex-col gap-5">
        <EditableText
          as="p"
          store="subtitle"
          className="type-label-s-caps"
          placeholder="Client"
          defaultValue="Client"
        />
        <div className="w-full grid grid-cols-2 gap-7">
          {props.clients?.map((client, index) => {
            const El = client?.link?.url ? Link : "div"
            const elProps = client?.link?.url
              ? {
                  href: client.link.url,
                  target: client.link.target || "_self",
                }
              : {}
            return (
              !!client && (
                <El key={index} {...elProps}>
                  <ResponsiveImage {...client.logo} className="w-full h-auto" />
                </El>
              )
            )
          })}
        </div>
      </div>
    </div>
  )

  return (
    <div className="w-full">
      <Themed theme={props.caseStudy?.pageTheme || "none"} className="w-full isolate relative themed-fg pt-7">
        {/* Background color */}
        <div className="absolute inset-0 -z-10 themed-bg" data-transition="fade"></div>
        <div className="w-full py-10">
          {props.flowgramImage?.mediaItemUrl ? (
            <div className="w-grid mx-auto flex flex-col items-start justify-center gap-7">
              {/* Flowgram image layout */}
              <div className="w-full grid grid-cols-1 md:grid-cols-2 border-2 border-current rounded-lg p-7">
                <div className="w-full h-full flex flex-col">
                  <div
                    className="flex flex-col justify-between gap-7 md:gap-4 self-stretch h-full"
                    data-transition="scale"
                  >
                    <div className="flex items-center gap-1" data-transition="children/fade-up">
                      {props.categories?.map(
                        (category, index) =>
                          !!category && (
                            <Button key={index} href={category.uri || ""} size="sm">
                              {category.name}
                            </Button>
                          ),
                      )}
                    </div>
                    <div className="w-full flex flex-col gap-4" data-transition="children/fade-up">
                      <p className="type-label-s-caps">Case Study</p>
                      <h1 className="type-title-m md:type-title-xl text-balance">
                        {env.admin ? (
                          <EditableText
                            as="span"
                            store={usePostTitleEditor()}
                            className=""
                            placeholder="Enter a title"
                          />
                        ) : (
                          props.caseStudy?.title
                        )}
                      </h1>
                      <h2 className="type-body-m md:type-title-s w-full text-balance" data-transition="fade-up">
                        {props.subtitle}
                      </h2>
                    </div>
                    <FlowgramImage flowgramImage={props.flowgramImage} className="w-[6.66em] shrink-0" />
                    <div className="flex md:hidden" />
                  </div>
                </div>
                <div
                  className="w-full flex items-center justify-center rounded-lg aspect-square overflow-hidden"
                  data-transition="scale"
                >
                  {props.featuredImages?.[0]?.mediaItemUrl && (
                    <ResponsiveImage
                      {...props.featuredImages[0]}
                      className="w-full h-full"
                      objectFit="cover"
                      data-transition="fade-up"
                    />
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="w-grid mx-auto flex flex-col items-start justify-center gap-7">
              {/* Standard layout */}
              <div className="w-full grid grid-cols-1 md:grid-cols-2">
                <div className="w-full h-full flex flex-col">
                  <div
                    className="flex flex-col justify-between gap-7 md:gap-4 rounded-lg border-2 border-current p-7 self-stretch h-full"
                    data-transition="scale"
                  >
                    <div className="flex items-center gap-1" data-transition="children/fade-up">
                      {props.categories?.map(
                        (category, index) =>
                          !!category && (
                            <Button key={index} href={category.uri || ""} size="sm">
                              {category.name}
                            </Button>
                          ),
                      )}
                    </div>
                    <div className="w-full flex flex-col gap-4" data-transition="children/fade-up">
                      <p className="type-label-s-caps">Case Study</p>
                      <h1 className="type-title-m md:type-title-xl text-balance">
                        {env.admin ? (
                          <EditableText
                            as="span"
                            store={usePostTitleEditor()}
                            className=""
                            placeholder="Enter a title"
                          />
                        ) : (
                          props.caseStudy?.title
                        )}
                      </h1>
                    </div>
                    <div className="hidden md:flex" />
                  </div>
                  <div className="w-full h-[25vw] md:h-[34.5%] shrink-0 relative">
                    <div className="absolute left-0 -top-[2px] w-full h-[calc(100%+2px)] flex flex-nowrap">
                      <div
                        className="h-full aspect-square border-2 border-current rounded-full shrink-0"
                        data-transition="scale"
                      />
                      <div className="h-full w-full relative">
                        <div
                          className="absolute -left-[2px] top-0 w-[calc(100%+2px)] h-full p-7 flex items-center justify-center border-2 border-current rounded-lg"
                          data-transition="scale"
                        >
                          <h2 className="type-body-m md:type-title-m w-full text-balance" data-transition="fade-up">
                            {props.subtitle}
                          </h2>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className={cn(
                    "w-full flex items-center justify-center rounded-lg aspect-square overflow-hidden themed-bg",
                    !props.featuredImages?.[0]?.mediaItemUrl &&
                      "border-2 border-current -mt-[2px] md:mt-0 md:-ml-[2px]",
                  )}
                  data-transition="scale"
                >
                  {props.featuredImages?.[0]?.mediaItemUrl && (
                    <ResponsiveImage
                      {...props.featuredImages[0]}
                      className="w-full h-full"
                      objectFit="cover"
                      fadeOnLoad
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </Themed>
      <div className="grid-auto py-9 gap-y-9 md:gap-y-7 md:py-7">
        {projectSummary && (
          <div className={cn("col-0-12 md:col-0-6 md:col-span-6 row-start-2 md:row-start-1")} data-transition="fade-up">
            {projectSummary}
          </div>
        )}
        {projectStats && (
          <div className={cn("col-0-12 md:col-6-6 md:col-span-6 row-start-1 md:row-start-1")} data-transition="fade-up">
            {projectStats}
          </div>
        )}
        {clientList && (
          <div
            className={cn("col-0-12 md:col-0-6", projectSummary ? "md:row-start-2" : "md:row-start-1")}
            data-transition="fade-up"
          >
            {clientList}
          </div>
        )}
        {projectScope && (
          <div className={cn("col-0-12 md:col-6-6", projectStats ? "md:row-start-2" : "md:row-start-1")}>
            {projectScope}
          </div>
        )}
      </div>
    </div>
  )
})

function StatBlock({ id, bg, fg }: { id: number; bg: string; fg: string }) {
  const heading = useInlineEditableValue<string>(`stat${id}`)
  const description = useInlineEditableValue<string>(`stat${id}Description`)
  const { isIntersecting, ref } = useIntersectionObserver({
    root: null,
    rootMargin: "0px",
    threshold: 0.1,
    freezeOnceVisible: true,
  })

  if (!env.admin && !heading[0] && !description[0]) {
    return null
  }

  return (
    <div className="w-full relative md:w-1/3 flex self-stretch" ref={ref}>
      <div
        className={cn(
          "self-stretch md:w-full flex flex-col rounded-lg p-5 md:flex-1 basis-1 max-md:w-[250px] max-md:flex-none",
          bg,
          fg,
        )}
      >
        <div className="flex flex-col gap-4">
          <EditableText as="p" store={heading} className="type-title-m" placeholder="50%" defaultValue="" />
          <EditableText
            as="p"
            store={description}
            className="type-body-xl text-pretty break-words hyphens-auto"
            placeholder="Statistic description"
          />
        </div>
      </div>
      {!env.admin && (
        <div
          className={cn(
            "hidden md:flex absolute w-full left-0 top-0 self-stretch flex-col rounded-lg p-5 md:flex-1 basis-1 max-md:w-[250px] max-md:flex-none transform transition-transform duration-1000 ease-in-out",
            bg,
            fg,
          )}
          style={{
            transform: isIntersecting ? "translateY(0)" : `translateY(-${20 + 5 * id}em)`,
            transitionDelay: `${id * 200}ms`,
          }}
        >
          <div className="flex flex-col gap-4">
            <EditableText as="p" store={heading} className="type-title-m" placeholder="50%" defaultValue="" />
            <EditableText
              as="p"
              store={description}
              className="type-body-xl text-pretty break-words hyphens-auto"
              placeholder="Statistic description"
            />
          </div>
          <div
            className={cn("w-full transition-all duration-1000 ease-in-out")}
            style={{ height: isIntersecting ? "0px" : `${20 + 5 * id}em`, transitionDelay: `${id * 200}ms` }}
          />
        </div>
      )}
    </div>
  )
}
